import { useCallback, useEffect, useState } from 'react';

import type { WidgetOnlineWeighingProps } from '../components/dashboard/Dashboard.types';

const STORAGE_KEY = 'dashboard.widgets';
// Fired by useDashboard on persist so the navbar feed updates same-tab
// (the native 'storage' event only fires in *other* tabs).
export const WIDGETS_CHANGED_EVENT = 'dashboard-widgets-changed';

const readScaleWidgets = (): WidgetOnlineWeighingProps[] => {
	if (typeof window === 'undefined') return [];
	try {
		const raw = localStorage.getItem(STORAGE_KEY);
		if (!raw) return [];
		const all = JSON.parse(raw) as WidgetOnlineWeighingProps[];
		return all.filter(
			(w) => w?.type === 'onlineWeighing' && w?.scale?.Id != null,
		);
	} catch {
		return [];
	}
};

/**
 * Online-weighing dashboard widgets the user has, kept in sync with storage.
 * Pass `enabled = false` on pages that never use this (e.g. row menus outside
 * the orders table) to skip the initial localStorage read and the listeners.
 */
const useScaleWidgets = (enabled = true): WidgetOnlineWeighingProps[] => {
	const [widgets, setWidgets] = useState<WidgetOnlineWeighingProps[]>(() =>
		enabled ? readScaleWidgets() : [],
	);

	const refresh = useCallback(() => setWidgets(readScaleWidgets()), []);

	useEffect(() => {
		if (!enabled) return;
		const onStorage = (e: StorageEvent) => {
			if (e.key === STORAGE_KEY) refresh();
		};
		window.addEventListener('storage', onStorage);
		window.addEventListener(WIDGETS_CHANGED_EVENT, refresh);
		return () => {
			window.removeEventListener('storage', onStorage);
			window.removeEventListener(WIDGETS_CHANGED_EVENT, refresh);
		};
	}, [enabled, refresh]);

	return widgets;
};

export default useScaleWidgets;
